import { useCallback, useMemo, useState } from 'react';
import { Box, IconButton, Tooltip, Typography } from '@mui/material';
import { MaterialReactTable } from 'material-react-table';
import { Trash2, CirclePlus, CircleMinus } from 'lucide-react';
import useInstanceReservationMutation from '../hooks/useInstanceReservationMutation';
import { ROW_ACTIONS_SX, TOOLBAR_ACTIONS_SX } from '@/shared/constants/tableIconButtons';
import StatusBadge from '@/shared/components/StatusBadge';
import { cx } from '@/styles/muiTheme';
import { useSnackbar } from 'notistack';

interface Props {
  sessions: Record<string, any>;
  onKillSession: (sessionId: string) => void;
  onChangeReservedStatus?: () => void;
}

// The exact hues the old solid pill used — only the shape changes. Anything
// that is neither ready nor deploying is amber, as before.
const getSessionStatusColor = (status: string) => {
  switch (status) {
    case 'ready':
      return '#4caf50';
    case 'deploying':
      return '#2196f3';
    default:
      return '#ff9800';
  }
};

/**
 * Finds a session by ID and returns the user ID and reserved flag
 * @param {string} targetSessionId - The session ID to search for
 * @param {Object} sessions - The sessions object
 * @returns {Object|null} - An object containing userId and reserved flag, or null if not found
 */
function findSessionById(targetSessionId: string, sessions: Record<string, any>) {
  // Iterate through all email keys in the sessions object
  for (const email in sessions) {
    const session = sessions[email];

    // Check if this session matches the target sessionId
    if (session.sessionId === targetSessionId) {
      // Extract the userId and reserved flag
      const userId = session.user.id;
      const isReserved = session.computeInstance?.reserved || false;

      return {
        userId,
        isReserved,
      };
    }
  }

  // Return null if no matching session is found
  return null;
}

const SessionsGrid = ({ sessions, onKillSession, onChangeReservedStatus }: Props) => {
  // Replace the global loading state with a Set to track loading states by session ID
  const [loadingSessionIds, setLoadingSessionIds] = useState<Set<string>>(new Set());
  const snackbar = useSnackbar();
  const reserveInstanceMutation = useInstanceReservationMutation();
  
  const handleReserveInstance = useCallback(
    async (sessionId: string) => {
      // Set loading state for this specific session
      setLoadingSessionIds(prev => new Set([...prev, sessionId]));
      
      try {
        const session = findSessionById(sessionId, sessions);
        if (session) {
          const { userId, isReserved } = session;
          await reserveInstanceMutation.mutateAsync({
            user: userId,
            operation: isReserved ? 'unreserve' : 'reserve',
          });
          if(onChangeReservedStatus){
            onChangeReservedStatus();
          }
          snackbar.enqueueSnackbar(`Instance reservation state changed successfully`, {
            variant: 'success',
            autoHideDuration: 3000,
          });
        } else {
          snackbar.enqueueSnackbar(`Session not found`, {
            variant: 'error',
            autoHideDuration: 3000,
          });
        }
      } catch (e) {
        console.error('Error reserving instance:', e);
        snackbar.enqueueSnackbar(`Error reserving instance: ${e.message || 'Unknown error'}`, {
          variant: 'error',
          autoHideDuration: 3000,
        });
      } finally {
        // Remove this session from loading state
        setLoadingSessionIds(prev => {
          const newSet = new Set([...prev]);
          newSet.delete(sessionId);
          return newSet;
        });
      }
    },
    [reserveInstanceMutation, sessions, onChangeReservedStatus, snackbar],
  );

  // Transform sessions object into array for MaterialReactTable
  const data = useMemo(() => {
    return Object.entries(sessions).map(([email, session]) => ({
      id: session.sessionId,
      email: email,
      handler: session.handler,
      status: session.status,
      reserved: session.computeInstance?.reserved ? 'X' : '',
      timezone: session.user.tz || 'Unknown',
      formattedLastActivity: session.formattedLastActivity || 'Unknown',
      lastActivity: session.lastActivity, // Used for sorting
    }));
  }, [sessions]);

  const columns = useMemo(
    () => [
      {
        accessorKey: 'handler',
        header: 'Handler',
        size: 130,
        muiTableBodyCellProps: { sx: { fontFamily: cx.fontMono } },
      },
      {
        accessorKey: 'email',
        header: 'Email',
        size: 250,
      },
      {
        accessorKey: 'status',
        header: 'Status',
        size: 150,
        Cell: ({ cell }) => {
          const status = String(cell.getValue() ?? '');
          return (
            <StatusBadge
              label={status}
              color={getSessionStatusColor(status)}
              // 'deploying' is work in flight, the same cue the clip statuses use.
              pulse={status === 'deploying'}
            />
          );
        },
      },
      {
        accessorKey: 'timezone',
        header: 'Timezone',
        size: 200,
      },
      {
        accessorKey: 'formattedLastActivity',
        header: 'Last Activity',
        size: 200,
        sortingFn: (rowA, rowB) => {
          return (rowA.original.lastActivity || 0) - (rowB.original.lastActivity || 0);
        },
      },
      {
        id: 'actions',
        header: 'Actions',
        // Was 250 for two text buttons; two 28px icon targets need far less.
        size: 120,
        // lucide icons render a plain <svg>, not MuiSvgIcon, so ROW_ACTIONS_SX's
        // `& .MuiSvgIcon-root { fontSize: 16 }` cannot reach them — hence the
        // explicit size on each, to match the MUI icons in the other tables.
        Cell: ({ row }) => (
          <Box sx={ROW_ACTIONS_SX}>
            <Tooltip
              title={row.original.reserved ? 'Unreserve instance' : 'Reserve instance'}
              arrow
            >
              <IconButton
                onClick={() => handleReserveInstance(row.original.id)}
                loading={loadingSessionIds.has(row.original.id)}
              >
                {row.original.reserved ? <CircleMinus size={16} /> : <CirclePlus size={16} />}
              </IconButton>
            </Tooltip>
            {/* Deliberately not muted like the other row actions: killing a
                session is destructive and irreversible, so it keeps the red.
                Kept last so the destructive action sits furthest from the
                reserve toggle. */}
            <Tooltip title="Kill session" arrow>
              <IconButton color="error" onClick={() => onKillSession(row.original.id)}>
                <Trash2 size={16} />
              </IconButton>
            </Tooltip>
          </Box>
        ),
      },
    ],
    [onKillSession, handleReserveInstance, loadingSessionIds],
  );

  return (
    <Box sx={{ height: 600, width: '100%' }}>
      <Typography variant="h6" sx={{ mb: 2 }}>
        Active Sessions
      </Typography>
      <MaterialReactTable
        columns={columns}
        data={data}
        // No enableColumnResizing: it flips MRT's layoutMode to 'grid-no-grow',
        // which lays rows and cells out as flex boxes with fixed widths. Every
        // other table in the app uses the default 'semantic' (a real <table>),
        // and that difference in padding and alignment model is what made this
        // one space differently.
        enablePagination
        enableColumnFilters
        enableSorting
        muiTopToolbarProps={{ sx: TOOLBAR_ACTIONS_SX }}
        initialState={{
          density: 'compact',
          pagination: { pageSize: 10, pageIndex: 0 },
          sorting: [{ id: 'formattedLastActivity', desc: true }],
        }}
        enableRowActions={false} // Disable the built-in row actions since we're using a custom column
        muiTableBodyRowProps={{ hover: true }}
        muiTablePaperProps={{ elevation: 0, sx: { borderRadius: 1 } }}
      />
    </Box>
  );
};

export default SessionsGrid;